/**
 * PesquisaJuridicaPage — hub da Pesquisa Jurisprudencial (F8).
 *
 * Réplica do legal_research.html: a consulta em texto livre, o histórico de
 * pesquisas salvas e o atalho para acompanhar as que ainda estão rodando.
 */

import {
  faBalanceScale,
  faCheckCircle,
  faExclamationTriangle,
  faEye,
  faMagnifyingGlass,
  faSpinner,
  faTrashAlt,
} from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { Link, useNavigate } from 'react-router'
import {
  criarPesquisa,
  excluirPesquisa,
  listarPesquisas,
} from '../../api/jurisprudencia'
import { useToast } from '../../components/Toast'

const TAMANHO_MINIMO = 3

export function PesquisaJuridicaPage() {
  const navegar = useNavigate()
  const clienteQuery = useQueryClient()
  const { notificar } = useToast()
  const [consulta, setConsulta] = useState('')

  const { data, isLoading } = useQuery({
    queryKey: ['pesquisas-juridicas'],
    queryFn: listarPesquisas,
  })

  const criar = useMutation({
    mutationFn: () => criarPesquisa(consulta.trim()),
    onSuccess: (r) => {
      void clienteQuery.invalidateQueries({ queryKey: ['pesquisas-juridicas'] })
      navegar(`/tasks/pesquisa/${r.id}`)
    },
    onError: (e: Error) => notificar('error', e.message),
  })

  const excluir = useMutation({
    mutationFn: (id: number) => excluirPesquisa(id),
    onSuccess: () => {
      void clienteQuery.invalidateQueries({ queryKey: ['pesquisas-juridicas'] })
      notificar('success', 'Pesquisa excluída.')
    },
    onError: (e: Error) => notificar('error', e.message),
  })

  const pesquisas = data?.pesquisas ?? []
  const curta = consulta.trim().length < TAMANHO_MINIMO

  return (
    <div className="mx-auto max-w-5xl">
      <header className="mb-6 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-lg dark:border-slate-700 dark:bg-dark-card">
        <div className="bg-gradient-to-r from-violet-600 via-purple-600 to-indigo-600 p-6 text-white">
          <h1 className="flex items-center text-2xl font-bold">
            <FontAwesomeIcon icon={faBalanceScale} className="mr-3" />
            Pesquisa Jurisprudencial
          </h1>
          <p className="mt-1 text-violet-100">
            Consulta a bases de jurisprudência e legislação, com análise dos
            precedentes aplicáveis ao caso.
          </p>
        </div>
        <form
          className="p-6"
          onSubmit={(e) => {
            e.preventDefault()
            if (curta) {
              notificar('error', 'Descreva a consulta com ao menos 3 caracteres.')
              return
            }
            criar.mutate()
          }}
        >
          <label
            htmlFor="consulta"
            className="mb-2 block text-sm font-medium text-slate-700 dark:text-slate-300"
          >
            O que você precisa pesquisar?
          </label>
          <textarea
            id="consulta"
            name="consulta"
            rows={4}
            value={consulta}
            onChange={(e) => setConsulta(e.target.value)}
            placeholder="Ex.: possibilidade de exigir atestado de capacidade técnica com quantitativo mínimo em pregão eletrônico"
            className="w-full rounded-lg border border-slate-300 px-4 py-3 text-slate-800 focus:outline-none focus:ring-2 focus:ring-violet-500 dark:border-slate-600 dark:bg-dark-bg dark:text-slate-100"
          />
          <button
            type="submit"
            disabled={criar.isPending}
            className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-violet-600 to-indigo-600 px-6 py-3 font-semibold text-white shadow-lg transition-all hover:from-violet-700 hover:to-indigo-700 disabled:opacity-60"
          >
            <FontAwesomeIcon
              icon={criar.isPending ? faSpinner : faMagnifyingGlass}
              spin={criar.isPending}
            />
            Pesquisar
          </button>
        </form>
      </header>

      <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-lg dark:border-slate-700 dark:bg-dark-card">
        <h2 className="mb-4 text-lg font-semibold text-slate-800 dark:text-slate-100">
          Pesquisas salvas
        </h2>
        {isLoading ? (
          <p className="text-sm text-slate-500">
            <FontAwesomeIcon icon={faSpinner} spin className="mr-2" />
            Carregando…
          </p>
        ) : pesquisas.length === 0 ? (
          <p className="rounded-lg border border-dashed border-slate-300 p-6 text-center text-sm text-slate-500 dark:border-slate-600">
            Nenhuma pesquisa ainda.
          </p>
        ) : (
          <ul className="space-y-3">
            {pesquisas.map((p) => {
              const pronta = p.status === 'CONCLUIDA'
              const falhou = p.status === 'FALHA' || !!p.erro
              return (
                <li
                  key={p.id}
                  className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-slate-200 p-4 dark:border-slate-700"
                >
                  <div className="min-w-0 flex-1">
                    <p className="truncate font-semibold text-slate-800 dark:text-slate-100">
                      {p.titulo}
                    </p>
                    <p className="truncate text-xs text-slate-500 dark:text-slate-400">
                      {p.criado_em} ·{' '}
                      <span
                        className={
                          falhou
                            ? 'text-red-600 dark:text-red-400'
                            : pronta
                              ? 'text-emerald-600 dark:text-emerald-400'
                              : 'text-amber-600 dark:text-amber-400'
                        }
                      >
                        <FontAwesomeIcon
                          icon={
                            falhou
                              ? faExclamationTriangle
                              : pronta
                                ? faCheckCircle
                                : faSpinner
                          }
                          spin={!pronta && !falhou}
                          className="mr-1"
                        />
                        {falhou
                          ? 'Falhou'
                          : pronta
                            ? 'Concluída'
                            : `Em andamento (${p.percentual}%)`}
                      </span>
                    </p>
                  </div>
                  <div className="flex shrink-0 gap-2">
                    <Link
                      to={pronta ? `/jurisprudencia/${p.id}` : `/tasks/pesquisa/${p.id}`}
                      className="inline-flex items-center gap-2 rounded-lg bg-slate-100 px-3 py-2 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-200 dark:bg-slate-700 dark:text-slate-200"
                    >
                      <FontAwesomeIcon icon={pronta ? faEye : faSpinner} />
                      {pronta ? 'Ver' : 'Acompanhar'}
                    </Link>
                    <button
                      type="button"
                      onClick={() => excluir.mutate(p.id)}
                      aria-label={`Excluir ${p.titulo}`}
                      className="rounded-lg border border-slate-200 px-3 py-2 text-sm text-red-600 transition-colors hover:bg-red-50 dark:border-slate-600 dark:hover:bg-red-900/20"
                    >
                      <FontAwesomeIcon icon={faTrashAlt} />
                    </button>
                  </div>
                </li>
              )
            })}
          </ul>
        )}
      </section>
    </div>
  )
}
